import { Button } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import { Blac } from "blac-next";
import CustomQuestionnaire from "components/CustomQuestionnaire/CustomQuestionnaire";
import CustomSuspense from "components/CustomSuspense/CustomSuspense";
import { CenterQuestionnaireContent } from "components/InAppQuestionnaire/InAppQuestionnaire";
import NotificationSettingsBloc from "components/NotificationSettings/NotificationSettingsBloc";
import Translate from "components/Translate/Translate";
import type { FC } from "react";
import React, { useEffect } from "react";
import { ONBOARDING_QUESTIONNAIRE_INITIAL_LAB } from "src/constants/misc";
import translate from "src/lib/translate";
import OnboardingBloc from "src/state/OnboardingBloc/OnboardingBloc";
import { KnownProgram } from "src/constants/programs";
import { useBloc } from "src/state/state";
import TaskManagementBloc from "src/state/TaskManagementBloc/TaskManagementBloc";
import InDialog from "src/ui/components/InDialog/InDialog";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import Track from "../../../Track/Track";
import { AppQueryPopupsController } from "../../AppQueryPopupsBloc";
import { BottomSheet } from "../../../BottomSheet/BottomSheet";
import InitialLabsBloc from "../InitialLabsBloc";
import SkipLabsConfirmation from "../../../SkipLabsConfirmation/SkipLabsConfirmation";
import MedicalInsuranceForm from "../../../MedicalInsuranceForm/MedicalInsuranceForm";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import FirstStep from "./FirstStep";
import StatusStep from "./StatusStep";

const Container = styled.div`
  max-width: 600px;
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100%;
  padding: var(--space-md);
  text-align: center;

  h1 {
    text-wrap: pretty;
  }
`;

const InitialLabDialog: FC<{
  returnUrl: string;
}> = ({ returnUrl }) => {
  const navigate = useGoToOrBack();
  const [, { handleInitialLabQuestionnaireCompleted }] =
    useBlocNext(OnboardingBloc);
  const [
    state,
    {
      updateTasks,
      setShowLearnMore,
      setShowSkipInitialLabsConfirmation,
      skipInitialLabs
    }
  ] = useBlocNext(InitialLabsBloc);

  const [, { togglePushNotifications, pushNotificationsEnabled }] = useBloc(
    NotificationSettingsBloc,
    {
      create: () => new NotificationSettingsBloc()
    }
  );
  const [taskState] = useBlocNext(TaskManagementBloc, {
    dependencySelector: (state) => [state.tasks]
  });

  const handleDismissLearnMoreModal = () => {
    setShowLearnMore(false);
  };

  const handleShowLearnMoreModal = () => {
    setShowLearnMore(true);
  };

  useEffect(() => {
    updateTasks();
  }, [taskState]);

  const onClose = () => {
    AppQueryPopupsController.closePopup();
    navigate(returnUrl, {
      multiBack: true,
      replace: true
    });
  };

  const onQuestionnaireSubmit = async (): Promise<boolean> => {
    const taskManagementState = Blac.getBloc(TaskManagementBloc);
    void taskManagementState.loadProgramTasks(KnownProgram.ONBOARDING);
    return false;
  };

  const handleSkipLabsButtonPress = () => {
    setShowSkipInitialLabsConfirmation(true);
  };

  const [firstStepFinished, setFirstStepFinished] = React.useState(false);
  const [, { firstName }] = useBlocNext(UserPreferencesCubit);

  const showFirstStep =
    (state.screen === "questionnaire" || state.screen === "medicalInsurance") &&
    !firstStepFinished;
  const showQuestionnaire =
    state.screen === "questionnaire" && firstStepFinished;
  const showMedicalInsurance =
    state.screen === "medicalInsurance" && firstStepFinished;
  const showStatus = state.screen === "status";

  return (
    <InDialog
      width="min(calc(100% - 4rem), 120rem)"
      height="min(calc(100% - 4rem), 70rem)"
      popup
      title={translate("task.consolidated_review_lab_status")}
      returnUrl={returnUrl}
      onClose={onClose}
    >
      {showFirstStep && (
        <FirstStep
          firstName={firstName ?? ""}
          onContinue={() => setFirstStepFinished(true)}
        />
      )}
      {showQuestionnaire && (
        <CenterQuestionnaireContent>
          <Track event="Initial Labs Dialog Questionnaire Shown" />
          <CustomSuspense>
            <CustomQuestionnaire
              resetScrollOnEachStep
              id={ONBOARDING_QUESTIONNAIRE_INITIAL_LAB}
              useRouting
              onDataSent={() => void handleInitialLabQuestionnaireCompleted()}
              onLastStepCompleted={() => void onQuestionnaireSubmit()}
            />
          </CustomSuspense>
        </CenterQuestionnaireContent>
      )}
      {showMedicalInsurance && (
        <Container>
          <Track event="Upload Medical Insurance Shown" />
          <MedicalInsuranceForm
            onStepChange={() => {}}
            onSaved={() => {
              const taskManagementState = Blac.getBloc(TaskManagementBloc);
              void taskManagementState.loadProgramTasks(
                KnownProgram.ONBOARDING
              );
              updateTasks();
            }}
            closePopupOnSave={false}
          />
        </Container>
      )}
      {showStatus && (
        <StatusStep
          state={state}
          pushNotificationsEnabled={pushNotificationsEnabled}
          onClose={onClose}
          onShowLearnMore={handleShowLearnMoreModal}
          onTogglePushNotifications={togglePushNotifications}
        />
      )}

      {state.showSkipInitialLabsConfirmation && (
        <SkipLabsConfirmation
          onClose={() => setShowSkipInitialLabsConfirmation(false)}
          onConfirm={() => void skipInitialLabs()}
          trackingName="Initial Labs Dialog Skip Confirmation"
        />
      )}

      <BottomSheet
        isOpen={state.showLearnMore}
        onClose={handleDismissLearnMoreModal}
        trackingName="Initial Labs Dialog Learn More"
        trackingData={{
          canSkipInitialLabsButton: state.canSkipInitialLabsButton,
          statusTitle: state.statusTitle
        }}
      >
        <Translate msg="labCategory.learnMore" markdown />

        {state.canSkipInitialLabsButton && (
          <>
            <Translate msg="labCategory.learnMoreAboutSkip" markdown />
            <nine-spacer s="md" />
            <Button
              onPress={handleSkipLabsButtonPress}
              theme="danger"
              outline
              grow
            >
              <Translate msg="skipInitialLabs" />
            </Button>
          </>
        )}
      </BottomSheet>
    </InDialog>
  );
};

export default InitialLabDialog;
